import React, { useEffect, useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2, Copy, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { TituloStatusBadge } from './TituloStatusBadge';

const money = (v) => (v === null || v === undefined || v === '')
  ? '—'
  : new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);
const day = (v) => {
  if (!v) return '—';
  const m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : String(v);
};

const Row = ({ label, value, mono }) => (
  <div className="flex items-start justify-between gap-3 border-b border-slate-100 py-2 last:border-0">
    <span className="text-xs uppercase tracking-wide text-slate-500">{label}</span>
    <span className={`text-sm text-right text-slate-900 ${mono ? 'font-mono text-xs break-all' : 'font-medium'}`}>{value}</span>
  </div>
);

export const TituloDetailDrawer = ({ titleId, open, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !titleId) return;
    setLoading(true);
    api.get(`/finance/titles/${titleId}/detail`)
      .then(r => setData(r.data))
      .catch(() => toast.error('Erro ao carregar detalhes do título'))
      .finally(() => setLoading(false));
  }, [open, titleId]);

  const t = data?.title;

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="titulo-detail-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            Detalhes do título
            {t && <TituloStatusBadge status={t.status} />}
          </SheetTitle>
        </SheetHeader>

        {loading || !t ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-violet-600" />
          </div>
        ) : (
          <div className="mt-4 space-y-6">
            <section>
              <h4 className="mb-1 text-xs font-bold uppercase tracking-wider text-violet-700">Família</h4>
              <Row label="Aluno" value={t.student_name || '—'} />
              <Row label="Responsável" value={t.responsible_name || '—'} />
              <Row label="Turma" value={t.class_group_name || '—'} />
            </section>

            <section>
              <h4 className="mb-1 text-xs font-bold uppercase tracking-wider text-violet-700">Cobrança</h4>
              <Row label="Descrição" value={t.description || '—'} />
              <Row label="Categoria" value={t.category || '—'} />
              <Row label="Competência" value={t.competence || '—'} />
              <Row label="Parcela" value={t.installment || '—'} />
              <Row label="Origem" value={<Badge variant="outline">{t.source_label || '—'}</Badge>} />
              <Row label="Plano" value={t.plan_id || '—'} />
            </section>

            <section>
              <h4 className="mb-1 text-xs font-bold uppercase tracking-wider text-violet-700">Valores</h4>
              <Row label="Valor original" value={money(t.amount_original)} />
              <Row label="Desconto" value={money(t.discount)} />
              <Row label="Multa" value={money(t.fine)} />
              <Row label="Juros" value={money(t.interest)} />
              <Row label="Pago" value={money(t.paid_amount)} />
              <Row label="Saldo" value={<span className={Number(t.balance) > 0 ? 'text-rose-700 font-bold' : 'text-emerald-700 font-bold'}>{money(t.balance)}</span>} />
            </section>

            <section>
              <h4 className="mb-1 text-xs font-bold uppercase tracking-wider text-violet-700">Datas e pagamento</h4>
              <Row label="Vencimento" value={day(t.due_date)} />
              <Row label="Pagamento" value={day(t.payment_date)} />
              <Row label="Forma" value={t.payment_method || '—'} />
              <Row label="Origem do pagamento" value={t.payment_origin || '—'} />
            </section>

            <section>
              <h4 className="mb-1 text-xs font-bold uppercase tracking-wider text-violet-700">Boleto</h4>
              <Row label="Banco" value={data.bill?.banco || (t.has_bill ? 'Safra' : '—')} />
              <Row label="Origem do boleto" value={
                t.boleto_origem === 'safra_importado'
                  ? <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-800" data-testid="drawer-boleto-origem">BOLETO SAFRA IMPORTADO</Badge>
                  : t.boleto_origem === 'remessa'
                    ? <Badge variant="outline" className="border-emerald-300 bg-emerald-50 text-emerald-800" data-testid="drawer-boleto-origem">BOLETO GERADO VIA REMESSA</Badge>
                    : '—'
              } />
              <Row label="Nosso número" value={t.nosso_numero || '—'} mono />
              <Row label="Linha digitável" value={t.linha_digitavel || '—'} mono />
              {t.linha_digitavel && (
                <div className="mt-2 flex gap-2">
                  <Button size="sm" variant="outline" data-testid="drawer-copy-linha"
                    onClick={() => { navigator.clipboard.writeText(t.linha_digitavel); toast.success('Linha digitável copiada!'); }}>
                    <Copy className="w-3.5 h-3.5 mr-1" /> Copiar linha
                  </Button>
                  <Button size="sm" variant="outline" data-testid="drawer-open-boleto"
                    onClick={async () => {
                      try {
                        const r = await api.get(`/financial/titles/${t.title_id}/boleto`, { responseType: 'blob' });
                        const url = URL.createObjectURL(r.data);
                        window.open(url, '_blank');
                        setTimeout(() => URL.revokeObjectURL(url), 60000);
                      } catch (e) {
                        toast.warning('Boleto PDF indisponível.');
                      }
                    }}>
                    <ExternalLink className="w-3.5 h-3.5 mr-1" /> Ver boleto
                  </Button>
                </div>
              )}
            </section>

            {data.cnab?.length > 0 && (
              <section>
                <h4 className="mb-1 text-xs font-bold uppercase tracking-wider text-violet-700">Retornos CNAB</h4>
                {data.cnab.map((c, i) => (
                  <Row key={i} label={day(c.data_pagamento || c.data_ocorrencia)} value={`${c.ocorrencia || c.status || '—'} · ${money(c.valor_pago || c.valor)}`} />
                ))}
              </section>
            )}

            {data.nfse && (
              <section>
                <h4 className="mb-1 text-xs font-bold uppercase tracking-wider text-violet-700">NFS-e</h4>
                <Row label="Número" value={data.nfse.numero || '—'} />
                <Row label="Situação" value={data.nfse.status || '—'} />
              </section>
            )}

            <section>
              <h4 className="mb-1 text-xs font-bold uppercase tracking-wider text-violet-700">Eventos</h4>
              {(!data.events || data.events.length === 0) ? (
                <p className="py-2 text-sm text-slate-400">Nenhum evento registrado.</p>
              ) : (
                <ul className="space-y-1.5" data-testid="drawer-events">
                  {data.events.map((e, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs">
                      <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-violet-500" />
                      <div>
                        <b className="text-slate-800">{e.event || e.type || e.event_type || 'EVENTO'}</b>
                        <span className="text-slate-500"> · {(() => {
                          const d = e.at || e.created_at || e.timestamp;
                          return d ? new Date(d).toLocaleString('pt-BR') : '—';
                        })()}</span>
                        {(e.amount ?? e.valor) !== undefined && (e.amount ?? e.valor) !== null && (
                          <span className="text-slate-500"> · {money(e.amount ?? e.valor)}</span>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default TituloDetailDrawer;
